import React, { useContext } from "react";
import Button from "@mui/material/Button";
import { ToasterContext } from "../../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../../constants/global";
import {
  CircularProgress,
  Modal,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
} from "@mui/material";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { OrderUserType } from "../../../constants/order";
import { useMutationUpdateManyOrder } from "../../../queries/order/hooks/useMutationUpdateManyOrder";
import axios from "axios";


export type CustomLabelKind = "newLabel" | "returnLabel";

interface OrderItemActionProps {
  user?: OrderUserType.buyer | OrderUserType.seller
  item: any;
  refetch?: any;
  refetchLoading?: boolean;
  isAdmin?: boolean;
  isColumn?: boolean;
  orders?: any[];
}

export const OrderItemActions: React.FunctionComponent<OrderItemActionProps> = ({
  user = OrderUserType.seller,
  item,
  refetch,
  refetchLoading = false,
  isAdmin = false,
  isColumn = false,
  orders
}) => {
  const { addToast } = useContext(ToasterContext);
  const [updateManyOrder, { loading: updateLoading }] = useMutationUpdateManyOrder();
  const [regeneratingChecklist, setRegeneratingChecklist] = React.useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = React.useState(false);
  const [customLabelDialogOpen, setCustomLabelDialogOpen] = React.useState(false);
  const [customLabelKind, setCustomLabelKind] = React.useState<CustomLabelKind>("newLabel");
  const [customLabelGenerating, setCustomLabelGenerating] = React.useState(false);
  /** Persisted PDF links come from DB via loadCustomLabelMeta (not session-only customLabelFileUrl). */
  const [customLabelMeta, setCustomLabelMeta] = React.useState<{
    adminFileName: string | null;
    returnFileName: string | null;
  }>({ adminFileName: null, returnFileName: null });

  const loadCustomLabelMeta = React.useCallback(async () => {
    if (!item?.orderNumber) return;
    try {
      const r = await axios.get(
        `${SERVER_URL}/v2/order/get/orderByOrderNumber?orderNumber=${encodeURIComponent(
          item.orderNumber
        )}`
      );
      const rows = r.data?.data;
      const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
      const row =
        list.find((o: { id?: number }) => o.id === item.id) ?? list[0];
      setCustomLabelMeta({
        adminFileName: row?.AdminLabel?.name ?? null,
        returnFileName: row?.ReturnLabel?.name ?? null,
      });
    } catch {
      setCustomLabelMeta({ adminFileName: null, returnFileName: null });
    }
  }, [item?.orderNumber, item?.id]);

  const handleBuyerConfirm = (event: any) => {
    event.stopPropagation();
    updateManyOrder({
      variables: {
        data: { shippingStatus: { set: 5 } },
        where: {
          orderNumber: {
            equals: item.orderNumber
          },
          trackingNumber: {
            equals: item.trackingNumber
          },
          stripeTransactionId: {
            equals: item.stripeTransactionId
          },
        }
      }
    }).then(() => {
      refetch();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    })
  }

  const handleAdminCancel = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(true);
  }

  const handleConfirmCancel = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(false);
    updateManyOrder({
      variables: {
        data: { status: { set: 0 } },
        where: {
          orderNumber: {
            equals: item.orderNumber
          },
        }
      }
    }).then(() => {
      refetch();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    })
  }

  const handleCancelConfirmationClose = (event: any) => {
    event.stopPropagation();
    setCancelConfirmationOpen(false);
  }

  const regenerateChecklist = async (orders: any[], trackingNumber: string) => {
    setRegeneratingChecklist(true);

    const orderNumber = orders[0]?.orderNumber;
    const orderDetailsApi = `${SERVER_URL}/v2/order/get/orderByOrderNumber?orderNumber=${orderNumber}`;
    const response = await axios.get(orderDetailsApi);
    const orderDetails = response.data.data[0];
    const shipDate = orderDetails?.shipDate;
    const pickupDate = orderDetails?.pickupDate;

    const api = `${SERVER_URL}/v2/checklist/generate`;
    const postData = {
      orders,
      trackingNumber,
      shipDate: new Date(shipDate).toISOString(),
      pickupDate: new Date(pickupDate).toISOString(),
    };
    axios
      .post(api, postData)
      .then((res) => {
        if (res.data.success) {
          addToast({ type: NotificationType.Success, title: "Checklist regenerated successfully!" });
          refetch();
        } else {
          addToast({ type: NotificationType.Error, title: "Failed to regenerate checklist." });
        }
      })
      .catch((error: any) => {
        console.error("Error regenerating checklist:", error);
        addToast({ type: NotificationType.Error, title: error.message });
      })
      .finally(() => {
        setRegeneratingChecklist(false);
      });
  };
  const openCustomLabelDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomLabelKind("newLabel");
    setCustomLabelDialogOpen(true);
  };

  React.useEffect(() => {
    if (customLabelDialogOpen && isAdmin) {
      loadCustomLabelMeta();
    }
  }, [customLabelDialogOpen, isAdmin, loadCustomLabelMeta]);

  const closeCustomLabelDialog = (
    _event?: unknown,
    _reason?: "backdropClick" | "escapeKeyDown"
  ) => {
    setCustomLabelDialogOpen(false);
    setCustomLabelGenerating(false);
  };

  const generateCustomLabel = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setCustomLabelGenerating(true);

    try {
      const api = `${SERVER_URL}/v2/order/admin/generate-custom-label`;
      const res = await axios.post(api, {
        orders: orders ?? [],
        kind: customLabelKind,
      });
      const payload = res.data;
      if (!payload?.success || !payload?.fileName) {
        const errMsg =
          typeof payload?.error === "string"
            ? payload.error
            : payload?.error?.message ?? "Failed to generate label.";
        throw new Error(errMsg);
      }
      addToast({
        type: NotificationType.Success,
        title:
          customLabelKind === "returnLabel"
            ? "Return label generated."
            : "Label generated.",
      });
      await refetch?.();
      await loadCustomLabelMeta();
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ??
        error?.response?.data?.message ??
        error?.message ??
        "Failed to generate label.";
      addToast({
        type: NotificationType.Error,
        title: typeof msg === "string" ? msg : "Failed to generate label.",
      });
    } finally {
      setCustomLabelGenerating(false);
    }
  };

  const selectedCustomLabelFileName =
    customLabelKind === "newLabel"
      ? customLabelMeta.adminFileName
      : customLabelMeta.returnFileName;

  const selectedCustomLabelUrl = selectedCustomLabelFileName
    ? `${SERVER_URL}/file/${encodeURIComponent(selectedCustomLabelFileName)}`
    : null;

  const handlePrintChecklist = (event: any) => {
    event.stopPropagation();
    const checklistUrl = `${SERVER_URL}/file/order-checklist-${item.orderNumber}.pdf`;
    axios.head(checklistUrl)
      .then((response) => {
        window.open(checklistUrl, '_blank');
      }).catch((error) => {
        console.error("Error fetching checklist:", error);
        addToast({ type: NotificationType.Error, title: "Checklist not found. Please regenerate it." });
      });
  }

  return (
    <>
      <Modal open={regeneratingChecklist}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backdropFilter: 'blur(6px)', // <- Blur effect
          backgroundColor: 'rgba(255, 255, 255, 0.1)', // <- Half-transparent dark overlay
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300, // Ensure it's above most elements
        }}>
          <CircularProgress />
        </div>
      </Modal>

      <Grid
        container
        spacing={2}
        sx={isColumn ?
          { p: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }
          : { p: 2, display: 'flex', justifyContent: 'end' }
        }
      >
        {item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Tooltip
              title={
                <>
                  <div>We're generating your label — please wait.</div>
                  <div>For assistance, contact support@tradenetrx.com</div>
                </>
              }
              arrow
            >
              <span style={{ display: 'block' }}>
                <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" disabled>
                  Preparing Shipping Label...
                </Button>
              </span>
            </Tooltip>
          </Grid>
        )}
        {(item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id))) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={(event) => {
              event.stopPropagation();
              regenerateChecklist(orders ?? [], item.trackingNumber);
            }}>
              Regenerate Checklist
            </Button>
          </Grid>
        )}
        {item.shippingStatus > 0 && item.orderNumber && !(item.status === 1 && item.shippingStatus === 1 && !item.trackingNumber && !(item.Label && item.Label.id)) && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Link>
              <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={handlePrintChecklist}>
                Print Checklist
              </Button>
            </Link>
          </Grid>
        )}
        {item.shippingStatus > 0 && item.trackingNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Link href={`${SERVER_URL}/file/order-shipping-label-${item.trackingNumber}.pdf`} target="_blank">
              <Button fullWidth sx={{ mr: 2 }} color='primary' variant="contained" onClick={(event: any) => event.stopPropagation()}>
                Print Shipping Label
              </Button>
            </Link>
          </Grid>
        )}
        {isAdmin && item.shippingStatus > 0 && item.trackingNumber && item.orderNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              fullWidth
              sx={{ mr: 2 }}
              color="primary"
              variant="contained"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={openCustomLabelDialog}
            >
              Custom label
            </Button>
          </Grid>
        )}
        {!isAdmin && item.shippingStatus === 4 && user === OrderUserType.buyer && (
          <Grid item xs={12} md={4} xl={2}>
            <Button
              sx={{ mr: 2 }}
              color='primary'
              variant="contained"
              onClick={(event: any) => handleBuyerConfirm(event)}
              fullWidth
              disabled={updateLoading || refetchLoading}
            >
              Confirm Order
              {updateLoading && <CircularProgress sx={{ ml: 2 }} size={14} color='inherit' />}
            </Button>
          </Grid>
        )}
        {item.shippingStatus === 0 && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              sx={{ mr: 2 }}
              color='primary'
              variant="contained"
              onClick={(event: any) => handleAdminCancel(event)}
              fullWidth
              disabled={updateLoading || refetchLoading}
            >
              Cancel Order
              {updateLoading && <CircularProgress sx={{ ml: 1 }} size={14} color='inherit' />}
            </Button>
          </Grid>
        )}
        {item.trackingNumber && (
          <Grid item xs={12} md={isColumn ? 12 : 4} xl={isColumn ? 12 : 2}>
            <Button
              color='primary'
              variant="contained"
              fullWidth
              onClick={(event: any) => {
                event.stopPropagation();
                window.open(`https://www.fedex.com/fedextrack/?trknbr=${item.trackingNumber}`, '_blank');
              }}
            >
              Track Order
            </Button>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={customLabelDialogOpen}
        onClose={closeCustomLabelDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Custom label</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={customLabelKind}
              onChange={(e) => {
                e.stopPropagation();
                setCustomLabelKind(e.target.value as CustomLabelKind);
              }}
            >
              <FormControlLabel
                value="newLabel"
                control={<Radio />}
                label="New label"
              />
              <FormControlLabel
                value="returnLabel"
                control={<Radio />}
                label="New return label"
              />
            </RadioGroup>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={customLabelGenerating}
              onClick={generateCustomLabel}
            >
              Generate
              {customLabelGenerating && (
                <CircularProgress size={16} sx={{ ml: 1 }} color="inherit" />
              )}
            </Button>
          </Box>
          {selectedCustomLabelUrl && (
            <Box sx={{ mt: 2 }}>
              <Link
                href={selectedCustomLabelUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {customLabelKind === "returnLabel"
                  ? "Open return label (PDF)"
                  : "Open admin label (PDF)"}
              </Link>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              closeCustomLabelDialog();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmationOpen} onClose={handleCancelConfirmationClose}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel order {item.orderNumber}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} variant="contained" color="primary">
            Yes
          </Button>
          <Button 
            onClick={handleCancelConfirmationClose} 
            variant="contained" 
            color="secondary"
            sx={{
              backgroundColor: '#6c757d',
              '&:hover': {
                backgroundColor: '#5a6268',
              },
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}